/**
 * VaccinationComplianceReport — pets with expired or unverified
 * vaccinations, grouped by vaccine type, sorted by next-visit date.
 *
 * Issue #32 AC2: "I see a list of all pets with non-compliant
 * vaccinations, grouped by vaccine type, sorted by next visit date."
 *
 * Data flows in from the Reports parent; pure presentational.
 */
import type { VaccinationComplianceReport as VaxReportData } from '../../lib/api.js';
// Sawyer 09 pet-inactivity (D1)
import { Alert } from '@ferro/ui';
import { InactiveDogsToggle } from './InactiveDogsToggle.js';
import { inactiveSinceLabel } from './inactive-since.js';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function vaccineLabel(name: string): string {
  return name
    .split('_')
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

export interface VaccinationComplianceReportProps {
  data: VaxReportData;
  // Sawyer 09 pet-inactivity (D1) — the switch lives with the page's URL state.
  includeInactive?: boolean;
  onIncludeInactiveChange?: (next: boolean) => void;
  busy?: boolean;
}

export function VaccinationComplianceReport({
  data,
  includeInactive = false,
  onIncludeInactiveChange,
  busy = false,
}: VaccinationComplianceReportProps): JSX.Element {
  const hiddenCount = data.inactiveDogsHidden ?? 0;
  return (
    <div className="ferro-report" data-testid="vax-compliance-report">
      {/* Sawyer 09 pet-inactivity (D1) — nothing renders when the rule is off or the server is older. */}
      {onIncludeInactiveChange ? (
        <InactiveDogsToggle
          idPrefix="reports-vax"
          months={data.inactivityMonths}
          hiddenCount={hiddenCount}
          includeInactive={includeInactive}
          onChange={onIncludeInactiveChange}
          busy={busy}
        />
      ) : null}
      {data.truncated ? (
        <Alert variant="warning">
          <span data-testid="vax-report-truncated">Showing the first 5,000 dogs. Some dogs are not on this list.</span>
        </Alert>
      ) : null}
      <div className="ferro-report__stat-grid">
        <article className="ferro-report__stat" data-testid="stat-compliant-pct">
          <span className="ferro-report__stat-label">Compliant</span>
          <span className="ferro-report__stat-value">{data.compliantPct}%</span>
          <span className="ferro-report__stat-meta">
            {data.compliantDogs} of {data.totalDogs} dogs
          </span>
        </article>
        <article
          className="ferro-report__stat ferro-report__stat--warn"
          data-testid="stat-flagged-dogs"
        >
          <span className="ferro-report__stat-label">Flagged dogs</span>
          <span className="ferro-report__stat-value">{data.flaggedDogs}</span>
          <span className="ferro-report__stat-meta">
            {data.totalNonCompliant} issue
            {data.totalNonCompliant === 1 ? '' : 's'} across vaccines
          </span>
        </article>
      </div>

      {data.groups.length === 0 ? (
        <div className="ferro-report__empty" role="status">
          {!includeInactive && hiddenCount > 0 ? (
            <p data-testid="vax-report-empty-hidden">
              No active dogs need attention. {hiddenCount} inactive {hiddenCount === 1 ? 'dog' : 'dogs'} hidden.
            </p>
          ) : (
            <p>All pets are vaccine-compliant. Nice.</p>
          )}
        </div>
      ) : (
        <div className="ferro-report__groups">
          {data.groups.map((g) => (
            <section
              key={g.vaccineName}
              className="ferro-report__group"
              data-testid={`vax-group-${g.vaccineName}`}
              aria-label={`${vaccineLabel(g.vaccineName)} non-compliant pets`}
            >
              <header className="ferro-report__group-head">
                <h3 className="ferro-report__group-title">
                  {vaccineLabel(g.vaccineName)}
                </h3>
                <span className="ferro-muted">
                  {g.issues.length} pet{g.issues.length === 1 ? '' : 's'}
                </span>
              </header>
              <ul className="ferro-report__list">
                {g.issues.map((issue) => (
                  <li
                    key={`${issue.dogId}-${issue.vaccineName}-${issue.issueType}`}
                    className="ferro-report__row"
                    data-testid={`vax-issue-${issue.dogId}-${issue.vaccineName}`}
                  >
                    <div>
                      <strong>{issue.dogName}</strong>
                      {inactiveSinceLabel(issue.inactiveSince) ? (
                        <span className="ferro-inactive-badge" data-testid="vax-inactive-badge">
                          {inactiveSinceLabel(issue.inactiveSince)}
                        </span>
                      ) : null}
                      <span className="ferro-muted">
                        {' '}
                        · {issue.breed ?? 'Unknown breed'} · owned by{' '}
                        {issue.ownerName}
                      </span>
                    </div>
                    <div className="ferro-report__row-meta">
                      <span
                        className={`ferro-report__badge ferro-report__badge--${
                          issue.issueType === 'expired' ? 'expired' : 'pending'
                        }`}
                      >
                        {issue.issueType === 'expired'
                          ? `Expired ${formatDate(issue.expiresAt)}`
                          : 'Unverified'}
                      </span>
                      <span className="ferro-muted">
                        Next visit: {formatDate(issue.nextVisitDate)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
